import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button, Grid } from "@mui/material";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import moment from "moment-timezone";

import SectionCard from "../../../components/SectionCard";
import { ErrorToast, SuccesToast } from "../../../components/Toast/message";
import { endPoints } from "../../../constant/Environment";
import { getAllData, addData } from "../../../Utility/API";
import { tokens } from "../../../theme";
import { mono, pill, th, td, ConfirmDialog } from "../../../components/tableKit";

const fmt = (d) => (d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm") : "—");

const levelKind = (level) => {
  switch (String(level || "").toUpperCase()) {
    case "CRITICAL":
    case "HIGH": return "bad";
    case "MEDIUM": return "warn";
    default: return "neutral";
  }
};

const reviewKind = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "BLOCKED": return "bad";
    case "PENDING":
    case "IN_REVIEW": return "warn";
    case "CLEARED": return "ok";
    default: return "neutral";
  }
};

const decisionKind = (d) => {
  switch (String(d || "").toUpperCase()) {
    case "BLOCK": return "bad";
    case "REVIEW": return "warn";
    default: return "neutral";
  }
};

/**
 * A player's risk standing on their detail page: score, review status, the
 * signals the engine raised and the limit changes recorded against them.
 *
 * The audit list is the point of this card — it is what makes a limit change or
 * a block reconstructable later, which the RG Limits panel above cannot show.
 */
export default function PlayerRiskCard({ userId }) {
  const [state, setState] = useState(null);
  const [events, setEvents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    const response = await getAllData(`${endPoints.api.RISK_PLAYER_DETAIL}${userId}`);
    if (response?.status === "success") {
      setState(response.data?.state || null);
      setEvents(response.data?.events || []);
      setAudit(response.data?.audit || []);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const setBlocked = async (blocked) => {
    const response = await addData(`${endPoints.api.RISK_PLAYERS}/${userId}/block`, {
      blocked,
      note: blocked ? "Blocked from player details" : "Unblocked from player details",
    });
    if (response?.data?.status === "success") {
      SuccesToast(response.data.message);
      load();
    } else {
      ErrorToast(response?.data?.message || "Could not update the player");
    }
    setConfirm(null);
  };

  const blocked = Boolean(state?.blocked);

  return (
    <SectionCard
      title="Risk & Responsible Gaming"
      subtitle="Engine score, signals raised, and the audit trail behind this player's limits"
      action={
        blocked ? (
          <Button size="small" variant="outlined" startIcon={<LockOpenRoundedIcon />}
            sx={{ textTransform: "none", color: tokens.secondary, borderColor: "rgba(35,214,160,0.4)" }}
            onClick={() => setConfirm("unblock")}>
            Unblock
          </Button>
        ) : (
          <Button size="small" variant="outlined" startIcon={<BlockRoundedIcon />}
            sx={{ textTransform: "none", color: tokens.danger, borderColor: "rgba(242,92,102,0.4)" }}
            onClick={() => setConfirm("block")}>
            Block betting
          </Button>
        )
      }
    >
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Typography sx={{ color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Risk score</Typography>
          <Typography sx={{ fontFamily: mono, fontSize: 22, fontWeight: 700 }}>{state?.risk_score ?? 0}</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography sx={{ color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, mb: 0.7 }}>Level</Typography>
          {pill(state?.risk_level || "LOW", levelKind(state?.risk_level))}
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography sx={{ color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, mb: 0.7 }}>Review</Typography>
          {pill(state?.review_status || "NONE", reviewKind(state?.review_status))}
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography sx={{ color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Last signal</Typography>
          <Typography sx={{ fontSize: 13, mt: 0.3 }}>{state?.last_event_type || "—"}</Typography>
          <Typography sx={{ fontFamily: mono, fontSize: 11, color: tokens.textDim2 }}>{fmt(state?.last_event_on)}</Typography>
        </Grid>
      </Grid>

      <Typography sx={{ color: tokens.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
        Recent risk events
      </Typography>
      <Box sx={{ overflowX: "auto", mb: 2.5 }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>When</th>
              <th style={th}>Rule</th>
              <th style={th}>Decision</th>
              <th style={th}>Stake</th>
              <th style={th}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td style={{ ...td, color: tokens.textDim }} colSpan={5}>No risk events for this player.</td></tr>
            )}
            {events.map((e) => (
              <tr key={e.id_risk_event}>
                <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{fmt(e.created_on)}</td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{e.rule_code || e.event_type}</td>
                <td style={td}>{pill(e.decision, decisionKind(e.decision))}</td>
                <td style={{ ...td, fontFamily: mono }}>{e.amount == null ? "—" : e.amount}</td>
                <td style={{ ...td, color: tokens.textDim, whiteSpace: "normal" }}>{e.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>

      <Typography sx={{ color: tokens.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, mb: 1 }}>
        Limit & risk audit history
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>When</th>
              <th style={th}>Action</th>
              <th style={th}>Detail</th>
              <th style={th}>From</th>
              <th style={th}>To</th>
              <th style={th}>By</th>
            </tr>
          </thead>
          <tbody>
            {audit.length === 0 && (
              <tr><td style={{ ...td, color: tokens.textDim }} colSpan={6}>No limit changes recorded.</td></tr>
            )}
            {audit.map((a) => (
              <tr key={a.id_risk_audit}>
                <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{fmt(a.created_on)}</td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11.5 }}>{a.action}</td>
                <td style={{ ...td, color: tokens.textDim, whiteSpace: "normal" }}>{a.reason || "—"}</td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: tokens.textDim2, whiteSpace: "normal" }}>{a.old_value || "—"}</td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, whiteSpace: "normal" }}>{a.new_value || "—"}</td>
                {/* performed_by is null when the player made the change themselves. */}
                <td style={td}>{a.performed_by ? `admin #${a.performed_by}` : "player"}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>

      <ConfirmDialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={confirm === "block" ? "Block player" : "Unblock player"}
        facts={[
          ["User id", String(userId)],
          ["Risk score", `${state?.risk_score ?? 0} (${state?.risk_level || "LOW"})`],
        ]}
        question={confirm === "block"
          ? "Block this player from placing any further bets?"
          : "Allow this player to bet again?"}
        note="Self-exclusion and cool-off are separate controls and are not changed by this. Recorded in the risk audit log against your admin account."
        actionLabel={confirm === "block" ? "Block" : "Unblock"}
        actionColor={confirm === "block" ? tokens.danger : tokens.secondary}
        onAction={() => setBlocked(confirm === "block")}
      />
    </SectionCard>
  );
}
